// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { actionStyles } from "@conklin/Overrides/Widgets/MyListsDetails/MyListsDetailsActions";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, modalButtonsActionStyles, moreActionsSharedStyle, xScreen } from "@conklin/XDL/Utilities";
import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import { css } from "styled-components";

actionStyles.wrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        gap: 22px;
        border-bottom: 1px solid ${color("common.border")};
        margin-bottom: 12px;
        padding-bottom: 12px;
        ${xScreen("lte", "md")} {
            flex-direction: column;
            gap: 0;
        }
    `,
};

actionStyles.actionsWrapperRight = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 20px;
        max-width: 50%;
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
        @media print {
            gap: 8px;
        }
    `,
};

actionStyles.buttonWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 15px;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            align-items: flex-start;
        }
    `,
};

actionStyles.listInfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 76px;
        ${xScreen("lte", "sm")} {
            gap: 8px;
            width: 100%;
            > div {
                width: 50%;
            }
        }
    `,
};

actionStyles.innerButtonWrapper = {
    css: css`
        display: flex;
        gap: 20px;
        align-items: flex-start;
        ${xScreen("lte", "sm")} {
            width: 100%;
            div,
            button {
                width: 100%;
            }
        }
    `,
};

actionStyles.statusContainer = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
        margin-bottom: 12px;
        @media print {
            gap: 8px;
            margin-bottom: 8px;
        }
    `,
};

actionStyles.addItemButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 8px 24px;
    `,
    variant: "secondary",
};
actionStyles.removeSelectedButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
    `,
    variant: "secondary",
};
actionStyles.addListButton = {
    css: css`
        width: fit-content;
        padding: 0 15px;
        white-space: nowrap;
    `,
};

actionStyles.editListModal = {
    sizeVariant: "small",
};
actionStyles.copyListModal = {
    sizeVariant: "small",
};

actionStyles.actionsWrapperLeft = {
    css: css`
        max-width: 50%;
        ${xScreen("lte", "md")} {
            max-width: none;
        }
    `,
};

actionStyles.listActionsWrapper = {
    css: css`
        margin-bottom: 20px;
        display: flex;
        flex-wrap: wrap;
        column-gap: 20px;
        row-gap: 12px;
        @media print {
            margin-bottom: 0;
        }
    `,
};

actionStyles.nameText = {
    variant: "h1",
    color: "text.main",
    css: css`
        display: block;
        margin-bottom: 16px;
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
    `,
};

actionStyles.wishlistCountWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,
};

actionStyles.wishlistLabel = {
    size: 14,
    lineHeight: "19px",
    weight: "bold",
};

actionStyles.wishlistCount = {
    size: 14,
    lineHeight: "19px",
};

actionStyles.iconContainer = {
    css: css`
        width: fit-content;
    `,
};
actionStyles.actionFavoriteClickable = {
    css: css`
        font-size: 12px;
        width: fit-content;
        line-height: 15px;
        color: ${color("text.link")};
    `,
};
actionStyles.starIcon = {
    src: Star,
    size: 12,
    css: css`
        margin-right: 6px;
    `,
};
actionStyles.starXIcon = {
    src: StarX,
    size: 12,
    css: css`
        margin-right: 6px;
    `,
};

actionStyles.overflowMenu = {
    ...moreActionsSharedStyle.overflowMenu,
};

actionStyles.moreActions = {
    ...moreActionsSharedStyle.moreActionsLink,
};

const customClickable = {
    css: css`
        font-size: 12px;
        line-height: ${fontConfig.lineHeight.small};
        font-weight: bold;
        color: ${color("text.link")};
        padding: 0;
        margin: 0;
    `,
};
actionStyles.printButton = {
    ...customClickable,
};
actionStyles.addTagsButton = {
    ...customClickable,
};
actionStyles.scheduleButton = {
    ...customClickable,
};
actionStyles.editButton = {
    ...customClickable,
};
actionStyles.shareButton = {
    ...customClickable,
};
actionStyles.hideButton = {
    ...customClickable,
};
actionStyles.copyButton = {
    ...customClickable,
};
actionStyles.exportButton = {
    ...customClickable,
};
actionStyles.deleteButton = {
    ...customClickable,
};

actionStyles.printListModal = {
    container: {
        gap: 0,
        css: css`
            ${GridItemStyle}:not(:last-child) {
                padding-bottom: 16px;
            }
        `,
    },
    productPropertiesCheckboxGridContainer: {
        gap: 0,
        css: css`
            ${GridOffset} {
                gap: 10px;

                ${xScreen("lte", "sm")} {
                    gap: 0;
                }
            }
            ${GridItemStyle} {
                padding: 10px 0;
            }
        `,
    },
    barcodeTypeRadioGroup: {
        horizontal: true,
        css: css`
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                align-items: baseline;
                gap: 10px;
            }
        `,
    },
    productPropertiesCheckboxGridItem: {
        width: 12,
        css: css`
            gap: 15px;

            ${xScreen("lte", "sm")} {
                flex-direction: column;
                align-items: baseline;
            }
        `,
    },
    buttonsGridItem: {
        ...modalButtonsActionStyles.buttonsGridItem,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    submitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
};
